/**
 * 온보딩 (spec §5): 시작하기 체크리스트의 건너뛰기·닫기, 화면별 안내 카드
 * 닫기, 아바타 메뉴의 "시작하기 다시 보기", 그리고 첫 방문 투어.
 *
 * 저장은 전부 POST /api/prefs 다 (§5.3) — 새 엔드포인트가 없다. 체크리스트
 * 문서는 서버가 카드의 data-doc 에 그대로 실어 보내므로, 여기서는 그 문서를
 * 고쳐 다시 저장한다: N-2 가 켠 first_action 이 보존된다.
 */

import { openExclusive, closedExclusive } from './popover-registry.js';
import { isPhone } from './phone.js';

const CHECKLIST = 'onboarding.checklist';
const TOUR = 'onboarding.tour';

function csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]');
    if (meta && meta.content) return meta.content;
    const input = document.querySelector('input[name="_csrf"]');
    if (input && input.value) return input.value;
    const m = document.cookie.match(/(?:^|;\s*)_csrf=([^;]+)/);
    return m ? decodeURIComponent(m[1]) : '';
}

/** POST /api/prefs — 빈 값은 삭제다 (prefs_api.go). */
async function savePref(key, value) {
    const token = csrfToken();
    const body = new URLSearchParams({ key, value: value == null ? '' : String(value), _csrf: token });
    const res = await fetch('/api/prefs', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json', 'X-CSRF-Token': token },
        body,
    });
    if (!res.ok) throw new Error('prefs ' + res.status);
}

function readDoc(card) {
    try { return JSON.parse(card.dataset.doc || '{}') || {}; } catch (_) { return {}; }
}

function tourData() {
    try { return JSON.parse(document.getElementById('onboardingData')?.textContent || '{}') || {}; } catch (_) { return {}; }
}

// 모드별 타깃 (Ruling 1). nav.mode 의 기본값이 header 라 스펙의 "사이드바" 만
// 걸면 기본 설치에서는 투어가 거의 뜨지 않는다 — 두 모드 모두에서 돈다.
// 타깃이 문서에 없는 단계는 빠지고, 살아남은 단계가 2개 미만이면 투어는
// 시작하지 않고 기록도 남기지 않는다(다음 기회에 다시 뜬다).
function tourSteps(d) {
    const L = d.labels || {};
    const header = d.mode === 'header';
    const defs = [
        {
            sel: header ? '.navbar-tabs [data-nav-tab="home"]' : '#adminSidebar [data-group="home"]',
            title: L.s1, body: L.s1b,
        },
        {
            sel: header
                ? '#adminSidebar .gnav-group[data-current] .gnav-head, .navbar-tabs'
                : '#adminSidebar .gnav-group[data-open] .gnav-head, #adminSidebar .gnav-head',
            title: L.s2, body: header ? L.s2b_header : L.s2b_sidebar,
        },
        { sel: '#notifBell, .user-menu-trigger', title: L.s3, body: L.s3b },
    ];
    return defs
        .map((s) => ({ ...s, el: document.querySelector(s.sel) }))
        .filter((s) => s.el && s.el.offsetParent !== null);
}

function shouldAutoStart(d) {
    if (!d.tour) return false;                 // 설정 꺼짐 또는 이미 본 사람
    if (isPhone()) return false; // §5.3 뷰포트 조건
    // 비밀번호 변경·TOTP 설정 강제 화면은 리다이렉트된 별도 화면이라 사이드바도
    // 탭도 없다 → 타깃이 0개가 되어 투어가 스스로 뜨지 않는다 (별도 표식 불필요).
    return true;
}

let tourState = null;

// 말풍선은 **설명하는 것을 가리지 않는다**: 대상과도, 상단바(.navbar)와도
// 겹치지 않는 자리를 고른다 — 헤더 모드의 첫 단계는 탭 줄을 가리키는데 그
// 위에 놓이면 말풍선이 그 탭의 클릭을 먹는다(오버레이는 클릭을 통과시키지만
// 말풍선 자체는 진짜 상자다).
//
// 후보는 아래 → 위 → 오른쪽 → 왼쪽 → 상단바 아래 순이고, 각 후보를 뷰포트
// 안으로 물린 뒤 방해물과 겹치는 넓이가 가장 작은 것을 고른다(같으면 앞선
// 후보). 대상이 화면 밖일 수 있어 먼저 보이게 하고 잰다 — 사이드바는 스크롤
// 위치를 기억해서 돌아온다(nav-scroll-memory).
const BUBBLE_GAP = 12;
const BUBBLE_EDGE = 12;

function overlapArea(a, b) {
    const w = Math.min(a.right, b.right) - Math.max(a.left, b.left);
    const h = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
    return w > 0 && h > 0 ? w * h : 0;
}

function placeBubble(bubble, target) {
    try { target.scrollIntoView({ block: 'nearest', inline: 'nearest' }); } catch (_) { /* 옛 브라우저 */ }
    const r = target.getBoundingClientRect();
    const bw = bubble.offsetWidth || 320;
    const bh = bubble.offsetHeight || 140;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const bar = document.querySelector('.navbar');
    const nav = bar ? bar.getBoundingClientRect() : null;
    const blockers = [r];
    if (nav && nav.width > 0 && nav.height > 0) blockers.push(nav);

    const clampX = (x) => Math.max(BUBBLE_EDGE, Math.min(x, Math.max(BUBBLE_EDGE, vw - bw - BUBBLE_EDGE)));
    const clampY = (y) => Math.max(BUBBLE_EDGE, Math.min(y, Math.max(BUBBLE_EDGE, vh - bh - BUBBLE_EDGE)));
    const box = (x, y) => {
        const left = clampX(x);
        const top = clampY(y);
        return { left, top, right: left + bw, bottom: top + bh };
    };

    const candidates = [
        box(r.left, r.bottom + BUBBLE_GAP),          // 아래 (기본)
        box(r.left, r.top - bh - BUBBLE_GAP),        // 위로 뒤집기
        box(r.right + BUBBLE_GAP, r.top),            // 오른쪽
        box(r.left - bw - BUBBLE_GAP, r.top),        // 왼쪽
    ];
    if (nav) candidates.push(box(r.left, nav.bottom + BUBBLE_GAP)); // 상단바 바로 아래

    let best = candidates[0];
    let bestScore = Infinity;
    for (const cand of candidates) {
        const score = blockers.reduce((sum, b) => sum + overlapArea(cand, b), 0);
        if (score < bestScore) { best = cand; bestScore = score; }
        if (bestScore === 0) break; // 겹치지 않는 첫 후보가 곧 답이다
    }
    bubble.style.left = best.left + 'px';
    bubble.style.top = best.top + 'px';
}

function endTour(value) {
    const st = tourState;
    tourState = null;
    if (!st) return;
    document.removeEventListener('keydown', st.onKey, true);
    window.removeEventListener('resize', st.onResize);
    st.spot?.classList.remove('onb-spot');
    st.overlay?.remove();
    st.bubble?.remove();
    closedExclusive('onboarding-tour');
    try { st.returnTo?.focus?.(); } catch (_) { /* 사라진 요소 */ }
    document.documentElement.dataset.tourState = value; // e2e 고정점
    savePref(TOUR, value).catch(() => { /* 다음 기회에 다시 뜬다 */ });
}

export function startTour(d) {
    if (tourState) return;
    const steps = tourSteps(d || {});
    if (steps.length < 2) return; // 기록도 남기지 않는다 (Ruling 1)
    const L = (d && d.labels) || {};
    const overlay = document.createElement('div');
    overlay.id = 'onboardingTour';
    overlay.className = 'onb-tour';
    const bubble = document.createElement('div');
    bubble.className = 'onb-bubble';
    bubble.setAttribute('role', 'dialog');
    bubble.setAttribute('aria-modal', 'true');
    bubble.setAttribute('aria-labelledby', 'onbTourTitle');
    bubble.innerHTML = '<h2 id="onbTourTitle"></h2><p></p><div class="onb-bubble-foot">'
        + '<span class="onb-bubble-count"></span>'
        + '<button type="button" class="btn btn-ghost btn-sm onb-skip"></button>'
        + '<button type="button" class="btn btn-secondary btn-sm onb-prev"></button>'
        + '<button type="button" class="btn btn-primary btn-sm onb-next"></button></div>';
    const state = { overlay, bubble, i: 0, steps, returnTo: document.activeElement, spot: null };
    // Tab 은 말풍선 안에 가둔다 (§7). Esc·바깥 클릭은 공용 팝오버 레지스트리가 처리한다.
    state.onKey = (e) => {
        if (!tourState || e.key !== 'Tab') return;
        const focusables = Array.from(bubble.querySelectorAll('button:not([hidden])'));
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (!bubble.contains(document.activeElement)) { e.preventDefault(); first.focus(); return; }
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    // 창 크기가 바뀌면 자리를 다시 고른다 — 한 번 고른 좌표는 새 뷰포트에서
    // 대상이나 상단바를 다시 덮을 수 있다.
    state.onResize = () => {
        if (!tourState) return;
        const step = state.steps[state.i];
        if (step && step.el) placeBubble(bubble, step.el);
    };
    const render = () => {
        const step = state.steps[state.i];
        state.spot?.classList.remove('onb-spot');
        state.spot = step.el;
        step.el.classList.add('onb-spot');
        bubble.querySelector('h2').textContent = step.title || '';
        bubble.querySelector('p').textContent = step.body || '';
        bubble.querySelector('.onb-bubble-count').textContent = (state.i + 1) + ' / ' + state.steps.length;
        const prev = bubble.querySelector('.onb-prev');
        prev.textContent = L.prev || '이전';
        prev.hidden = state.i === 0;
        bubble.querySelector('.onb-skip').textContent = L.skip || '건너뛰기';
        const next = bubble.querySelector('.onb-next');
        next.textContent = state.i === state.steps.length - 1 ? (L.done || '완료') : (L.next || '다음');
        placeBubble(bubble, step.el);
        next.focus();
    };
    bubble.addEventListener('click', (e) => {
        if (e.target.closest('.onb-skip')) { endTour('dismissed'); return; }
        if (e.target.closest('.onb-prev')) { state.i = Math.max(0, state.i - 1); render(); return; }
        if (e.target.closest('.onb-next')) {
            if (state.i === state.steps.length - 1) { endTour('done'); return; }
            state.i += 1;
            render();
        }
    });
    document.body.appendChild(overlay);
    document.body.appendChild(bubble);
    document.addEventListener('keydown', state.onKey, true);
    window.addEventListener('resize', state.onResize);
    tourState = state;
    // 배타성: Esc 와 바깥 클릭(= 말풍선 밖 어디든, 오버레이 포함)이 여기서 끝난다.
    openExclusive('onboarding-tour', () => endTour('dismissed'), { roots: ['.onb-bubble'] });
    render();
}

let reopenWired = false;

export function setupOnboarding() {
    const card = document.getElementById('onboardingChecklist');
    if (card && card.dataset.bound !== '1') {
        card.dataset.bound = '1';
        card.addEventListener('click', async (e) => {
            const skip = e.target.closest('[data-skip-step]');
            const close = e.target.closest('#onboardingClose');
            if (!skip && !close) return;
            const doc = readDoc(card);
            if (skip) {
                doc.skipped = Array.isArray(doc.skipped) ? doc.skipped : [];
                if (!doc.skipped.includes(skip.dataset.skipStep)) doc.skipped.push(skip.dataset.skipStep);
            } else {
                doc.dismissed = true;
            }
            card.dataset.doc = JSON.stringify(doc);
            try { await savePref(CHECKLIST, JSON.stringify(doc)); } catch (_) { /* 저장 실패는 조용히 — 다음 렌더에 원래대로 돌아온다 */ }
            if (close) { card.remove(); return; }
            const row = skip.closest('.onboarding-step');
            if (row) row.remove();
            if (!card.querySelector('.onboarding-step')) card.remove();
        });
    }

    const intro = document.querySelector('[data-intro-card]');
    if (intro && intro.dataset.bound !== '1') {
        intro.dataset.bound = '1';
        intro.querySelector('[data-intro-dismiss]')?.addEventListener('click', async () => {
            const key = intro.dataset.introCard;
            intro.remove();
            try { await savePref('onboarding.intro.' + key, 'dismissed'); } catch (_) { /* 다음 렌더에 다시 온다 */ }
        });
    }

    // 「시작하기 다시 보기」: 아바타 메뉴와 모바일 「전체」 시트 두 곳에 있다 (spec 2026-09-23 §2.4).
    // 시트는 SPA 리전이라 innerHTML 이 갈리므로 document 에 한 번만 위임한다.
    if (!reopenWired) {
        reopenWired = true;
        document.addEventListener('click', async (e) => {
            const btn = e.target.closest('[data-reopen-onboarding]');
            if (!btn) return;
            e.preventDefault();
            const liveCard = document.getElementById('onboardingChecklist');
            const doc = liveCard ? readDoc(liveCard) : {};
            // dismissed·skipped·completed_at 만 비운다 — first_action 은 유지 (§5.2).
            const next = { first_action: doc.first_action === true };
            try {
                await savePref(CHECKLIST, JSON.stringify(next));
                await savePref(TOUR, '');
                const key = document.querySelector('[data-intro-card]')?.dataset.introCard;
                if (key) await savePref('onboarding.intro.' + key, '');
            } catch (_) { /* 아래 새로고침이 서버 판정을 그대로 보여 준다 */ }
            window.location.reload(); // 같은 화면을 서버 판정으로 다시 그린다
        });
    }

    const d = tourData();
    const startBtn = document.querySelector('[data-tour-start]');
    if (startBtn && startBtn.dataset.bound !== '1') {
        startBtn.dataset.bound = '1';
        startBtn.addEventListener('click', () => startTour(d));
    }
    if (shouldAutoStart(d) && document.documentElement.dataset.tourStarted !== '1') {
        document.documentElement.dataset.tourStarted = '1'; // SPA 이동으로 두 번 뜨지 않게
        startTour(d);
    }
}
